import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const faqs = [
  { q: "When does Ujjain Maha Kumbh 2028 begin?", a: "The Simhastha Kumbh Mela 2028 begins on April 14, 2028 (Baisakhi Purnima) with the first Shahi Snan and continues for approximately 30 days." },
  { q: "Where exactly is the event held?", a: "The Kumbh Mela takes place in Ujjain, Madhya Pradesh, on the sacred banks of the Kshipra (Shipra) river. The main bathing ghat is Ram Ghat, near the Mahakaleshwar Temple." },
  { q: "How do I get to Ujjain?", a: "You can reach Ujjain by train (Ujjain Junction, UJN), by road via NH-52 from Indore (55 km), or by flying to Devi Ahilya Bai Holkar Airport in Indore (60 km away)." },
  { q: "Is registration mandatory?", a: "Pre-registration is highly recommended to ensure a smooth experience and receive official updates about schedules, accommodation, and facilities." },
  { q: "What facilities will be available?", a: "The event will have dedicated medical camps, police outposts, lost-and-found centers, drinking water stations, sanitation facilities, dharamshalas, and temporary accommodation camps." },
  { q: "What is Simhastha Kumbh Mela?", a: "Simhastha Kumbh Mela is a major Hindu pilgrimage and festival held every 12 years in Ujjain, Madhya Pradesh. It is one of the four sacred Kumbh Melas and is associated with the placement of Jupiter (Guru) in Simha (Leo) rashi." },
];

const timeline = [
  { phase: "Pre-Event", date: "Jan–Mar 2028", events: ["Pilgrim registration opens", "Infrastructure development", "Akhada camps set up", "Volunteer recruitment"] },
  { phase: "Main Event", date: "Apr–May 2028", events: ["April 14 — First Shahi Snan (Baisakhi)", "April 22 — Second Shahi Snan (Akshaya Tritiya)", "April 27 — Third Shahi Snan (Purnima)", "Daily Ganga Aarti at Ram Ghat"] },
  { phase: "Cultural", date: "Throughout April", events: ["Classical music & dance performances", "Spiritual discourses (Pravachans)", "Yoga & meditation camps", "Handicraft and food melas"] },
];

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }} viewport={{ once: true }}
      className="border border-white/8 rounded-2xl overflow-hidden"
      data-testid={`faq-item-${index}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-white/3 transition-all"
        data-testid={`faq-toggle-${index}`}>
        <span className="font-heading font-medium text-white text-base">{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} className="shrink-0">
          <ChevronDown size={18} className="text-saffron" />
        </motion.div>
      </button>
      {open && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
          className="px-6 pb-5 font-body text-white/55 text-sm leading-relaxed border-t border-white/5">
          <div className="pt-4">{a}</div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default function About() {
  return (
    <div className="min-h-screen bg-stone-900 pt-28 pb-20" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-20 mb-16 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img src="https://images.pexels.com/photos/6103203/pexels-photo-6103203.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Diya" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/70 to-stone-900" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">About the Event</p>
            <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mb-6">
              Simhastha<br /><span className="text-gradient-saffron">Kumbh 2028</span>
            </h1>
            <p className="font-body text-white/60 text-lg max-w-2xl mx-auto">
              The world's greatest spiritual congregation — where millions seek divine blessing, liberation, and moksha on the banks of the holy Kshipra.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Key Facts */}
        <section className="mb-20" data-testid="key-facts-section">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { value: "12 Years", label: "Sacred Cycle" },
              { value: "Apr 2028", label: "Event Date" },
              { value: "5 Crore+", label: "Pilgrims Expected" },
              { value: "30+ Days", label: "Duration" },
            ].map(({ value, label }, i) => (
              <motion.div key={label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                className="glass-card rounded-2xl p-5 text-center">
                <p className="font-heading font-bold text-2xl sm:text-3xl text-white mb-1">{value}</p>
                <p className="font-body text-white/40 text-xs uppercase tracking-widest">{label}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Significance */}
        <section className="mb-20" data-testid="significance-section">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-8">
            <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">Spiritual Significance</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Why Ujjain?</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: "Mahakaleshwar Jyotirlinga", desc: "Ujjain is home to the sacred Mahakaleshwar — one of the 12 Jyotirlingas and the only self-manifested (Swayambhu) Shivalinga. Lord Shiva here is the destroyer of Time itself." },
              { title: "Holy Kshipra River", desc: "The Kshipra (Shipra) river is considered as sacred as the Ganga during Kumbh Mela. Taking a dip here during the auspicious astronomical configuration grants moksha (liberation)." },
              { title: "Simhastha Yoga", desc: "The rare celestial alignment when Jupiter (Guru) enters Simha (Leo) rashi and the Sun enters Mesh (Aries) creates the most powerful period for spiritual purification." },
              { title: "Ancient City", desc: "Ujjain, also known as Avantika, is one of the seven sacred cities (Sapta Puri) of Hinduism. It has been a center of learning, astronomy, and devotion for thousands of years." },
            ].map(({ title, desc }, i) => (
              <motion.div key={title} initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                className="glass-card rounded-2xl p-6">
                <h3 className="font-heading font-semibold text-saffron text-lg mb-3">{title}</h3>
                <p className="font-body text-white/55 text-sm leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="mb-20" data-testid="timeline-section">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-8">
            <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">Schedule</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Event Timeline</h2>
          </motion.div>
          <div className="space-y-6">
            {timeline.map(({ phase, date, events }, i) => (
              <motion.div key={phase} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-saffron" />
                  <h3 className="font-heading font-semibold text-white text-lg">{phase}</h3>
                  <span className="ml-auto text-saffron/70 text-sm font-body">{date}</span>
                </div>
                <ul className="space-y-2 pl-6">
                  {events.map(ev => (
                    <li key={ev} className="flex items-start gap-2">
                      <span className="text-saffron/40 mt-1.5 shrink-0">–</span>
                      <span className="font-body text-white/55 text-sm">{ev}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Akhadas */}
        <section className="mb-20" data-testid="akhadas-section">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-8">
            <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">Holy Orders</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">The 13 Akhadas</h2>
            <p className="font-body text-white/50 mt-3 text-sm">The Akhadas are ancient monastic orders of Hindu ascetics. Their procession (Shahi Snan) during Kumbh is a magnificent sight.</p>
          </motion.div>
          <div className="flex flex-wrap gap-2">
            {["Niranjani Akhada","Juna Akhada","Mahanirvani Akhada","Atal Akhada","Avahan Akhada","Agni Akhada","Anand Akhada","Nirmohi Akhara","Digambar Akhara","Nirvani Akhara","Vaishnav Akhada","Udasin Akhada","Nirmal Akhada"].map((a, i) => (
              <motion.span key={a} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
                transition={{ delay: i * 0.04 }} viewport={{ once: true }}
                className="px-3 py-1.5 rounded-full text-xs font-body border border-white/10 text-white/50 hover:border-saffron/30 hover:text-saffron/80 transition-colors cursor-default">
                {a}
              </motion.span>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section data-testid="faq-section">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-8">
            <p className="text-saffron text-sm uppercase tracking-widest font-body mb-3">Help</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white">Frequently Asked Questions</h2>
          </motion.div>
          <div className="space-y-3">
            {faqs.map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} index={i} />)}
          </div>
        </section>
      </div>
    </div>
  );
}
